import React, { useState } from 'react';
import { Modal, Input, Label, Alert, Spinner } from 'reactstrap';
import { motion } from 'framer-motion';
import Select from 'react-select';
import api from '../helpers/api';
import { countryOptions } from '../constants/utils';
import { useTranslation } from 'react-i18next';

const InviteUserModal = ({ isOpen, toggle, onSuccess }) => {
    const { t } = useTranslation();

    const roleOptions = [
        { value: 'ROLE_FULL_USER', label: t('shared.role_full_user', 'Full User') },
        { value: 'ROLE_ADMINISTRATOR', label: t('shared.role_administrator', 'Administrator') },
        { value: 'ROLE_REQUESTOR', label: t('shared.role_requester', 'Requestor') },
    ];

    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [invitePhoneCode, setInvitePhoneCode] = useState('');
    const [invitePhone, setInvitePhone] = useState('');
    const [inviteRole, setInviteRole] = useState('ROLE_FULL_USER');
    const [createLoading, setCreateLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    const resetForm = () => {
        setInviteName('');
        setInviteEmail('');
        setInvitePhoneCode('');
        setInvitePhone('');
        setInviteRole('ROLE_FULL_USER');
        setApiError('');
        setCreateLoading(false);
    };

    const handleClose = () => {
        resetForm();
        toggle();
    };

    const handleCreate = async () => {
        setCreateLoading(true);
        setApiError('');
        try {
            if (!inviteName.trim()) {
                setApiError(t('shared.name_is_required', 'Name is required'));
                setCreateLoading(false);
                return;
            }
            if (!inviteEmail.trim()) {
                setApiError(t('shared.email_is_required', 'Email is required'));
                setCreateLoading(false);
                return;
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
                setApiError(t('shared.invalid_email', 'Please enter a valid email address'));
                setCreateLoading(false);
                return;
            }

            const payload = {
                name: inviteName.trim(),
                email: inviteEmail.trim(),
                targetRole: inviteRole,
            };

            if (invitePhone.trim()) {
                payload.phoneCode = invitePhoneCode.trim();
                payload.contactNumber = invitePhone.trim();
            }

            await api.post('invitation/create', payload);
            resetForm();
            if (onSuccess) onSuccess();
            toggle();
        } catch (error) {
            setApiError(error?.response?.data?.error || error?.response?.data?.message || t('business.invitations.failed_create', 'Failed to create invitation'));
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
            >
                <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title fw-semibold">{t('shared.invite_user', 'Invite User')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                    ></button>
                </div>
                <div className="modal-body pt-2">
                    <div className="mb-3">
                        <Label className="form-label">{t('shared.name', 'Name')}</Label>
                        <Input
                            type="text"
                            placeholder={t('shared.enter_name', 'Enter name')}
                            value={inviteName}
                            onChange={(e) => setInviteName(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <Label className="form-label">{t('shared.email_address', 'Email Address')}</Label>
                        <Input
                            type="email"
                            placeholder={t('shared.enter_recipient_email', "Enter recipient's email")}
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <Label className="form-label">{t('shared.phone_number', 'Phone Number')} <span className="text-muted">({t('shared.optional', 'optional')})</span></Label>
                        <div className="d-flex gap-2">
                            <Select
                                options={countryOptions}
                                value={countryOptions.find((c) =>
                                    invitePhoneCode === c.dialCode.replace('+', '')
                                ) || null}
                                onChange={(selected) => {
                                    setInvitePhoneCode(selected?.dialCode.replace('+', '') || '');
                                }}
                                filterOption={(option, inputValue) => {
                                    if (!inputValue) return true;
                                    const searchText = inputValue.toLowerCase();
                                    const labelText = option.data.labelText?.toLowerCase() || '';
                                    const name = option.data.name?.toLowerCase() || '';
                                    const dialCode = option.data.dialCode?.toLowerCase() || '';
                                    return labelText.includes(searchText) ||
                                        name.includes(searchText) ||
                                        dialCode.includes(searchText);
                                }}
                                className="flex-grow-0"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        width: '150px',
                                    }),
                                }}
                                isClearable
                                placeholder={t('shared.code', 'Code')}
                            />
                            <Input
                                type="tel"
                                className="flex-grow-1"
                                placeholder={t('shared.phone_number', 'Phone number')}
                                value={invitePhone}
                                onChange={(e) => setInvitePhone(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <Label className="form-label">{t('shared.role', 'Role')}</Label>
                        <Input
                            type="select"
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value)}
                        >
                            {roleOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Input>
                    </div>

                    {apiError && (
                        <Alert color="danger" className="mt-3">
                            {apiError}
                        </Alert>
                    )}
                </div>
                <div className="modal-footer border-0 pt-0">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleClose}
                    >
                        {t('shared.close', 'Close')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCreate}
                        disabled={createLoading}
                    >
                        {createLoading ? <Spinner size="sm" /> : t('shared.send_invitation', 'Send Invitation')}
                    </button>
                </div>
            </motion.div>
        </Modal>
    );
};

export default InviteUserModal;
